/* eslint-disable @typescript-eslint/no-explicit-any */
import { type PropsWithChildren } from 'react';

import { routeConfig } from '@rtkwlf/c3-feature-tasks';
import {
  RouterProvider as C3RouterProvider,
  type LinkProps,
  type PrimitiveLinkProps,
  createRouteStore,
} from '@rtkwlf/c3-lib-router';
import {
  type Params,
  Link as RRLink,
  generatePath,
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

/* -------------------------------------------------------------------------------------------------
 * Components
 * -----------------------------------------------------------------------------------------------*/
const C3Link = ({ to, hash, params, search, ref, ...props }: LinkProps) => {
  /**
   * Get an instance of the current url params
   */
  const rootParams = useParams();

  return (
    <RRLink
      {...props}
      to={computeLinkProps({
        to,
        hash,
        params,
        search,
        rootParams,
      })}
      ref={ref}
    />
  );
};

/* -------------------------------------------------------------------------------------------------
 * Constants
 * -----------------------------------------------------------------------------------------------*/
const routes = createRouteStore(routeConfig, {
  'organization.profile': '/:orgId',
  'organization.tasks.list.arcticWolf': '/:orgId/tasks/list/arcticwolf',
  'organization.tasks.list.outstanding': '/:orgId/tasks/list/outstanding',
  'organization.tasks.list.organization': '/:orgId/tasks/list/organization',
  'tasks.detail.attachments': '/:orgId/tasks/:taskId/attachments',
  'tasks.detail.comments': '/:orgId/tasks/:taskId/comments',
  'tasks.list.outstanding': 'n/a',
  'tasks.list.arcticWolf': 'n/a',
  'tasks.list.organization': 'n/a',
});

/* -------------------------------------------------------------------------------------------------
 * Utils
 * -----------------------------------------------------------------------------------------------*/
const computeLinkProps = ({
  to,
  search,
  params,
  rootParams,
  hash,
}: PrimitiveLinkProps & { rootParams: Params<string> }) => {
  return {
    pathname: generatePath(to, { ...rootParams, ...(params as any) }),
    search: search ? `?${new URLSearchParams(search).toString()}` : undefined,
    hash,
  };
};

/* -------------------------------------------------------------------------------------------------
 * RouterProvider
 * -----------------------------------------------------------------------------------------------*/
const RouterProvider = ({ children }: PropsWithChildren) => {
  /**
   * Get an instance of navigate
   */
  const navigate = useNavigate();

  /**
   * Get the current path name
   */
  const location = useLocation();

  /**
   * Get an instance of the current url params
   */
  const rootParams = useParams();

  return (
    <C3RouterProvider
      routes={routes}
      pathname={location.pathname}
      matchPath={matchPath}
      navigate={(props) => {
        return navigate(computeLinkProps({ ...props, rootParams }));
      }}
      Link={C3Link}
    >
      {children}
    </C3RouterProvider>
  );
};

export { RouterProvider, routes };
